/**
 * <cart-upgrade> — "Buy 2, Get 1 Free". Swaps the cart's 10-pack subscription
 * line(s) for the 30-pack subscription: removes each line and re-adds it as the
 * 30-pack variant with the 30-pack's Appstle selling plan, then refreshes the
 * cart drawer. Line data is embedded server-side (see cart-upgrade.liquid); the
 * display only drives the line's selling_plan — Appstle handles the actual
 * subscription/discount at checkout. `orig`/`origPlan` restore the 10-pack sub
 * if the add fails.
 */
class CartUpgrade extends HTMLElement {
  constructor() {
    super();
    this.sellingPlanId = this.dataset.sellingPlanId;
    this.productId = String(this.dataset.productId || '');
  }

  connectedCallback() {
    // Delegate the click to the element itself. Binding to a cached child button
    // fails when the element upgrades before its children are parsed (which
    // happens on the cart's Sections-API re-render) — then the button "does
    // nothing". Delegation is immune to that timing.
    if (this._bound) return;
    this._bound = true;
    this.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-cart-upgrade-btn]');
      if (btn && this.contains(btn)) {
        e.preventDefault();
        this.upgrade();
      }
    });
  }

  /* One-time lines eligible for upgrade, embedded server-side so we don't have
     to fetch the cart first. Each: {key, variant, quantity, product, plan}. */
  readLines() {
    const el = this.querySelector('[data-cart-upgrade-lines]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  /* Purchasable Appstle plan id: the server-rendered plan (Theme settings
     "Active plan ID" -> variant allocation) is authoritative. Only fall back to
     a captured localStorage value if the server gave us nothing. */
  planFor(line) {
    if (line.plan != null && line.plan !== '') return String(line.plan);
    if (this.sellingPlanId) return String(this.sellingPlanId);
    try {
      return localStorage.getItem('soar:sellingPlan:' + (line.product || this.productId)) || '';
    } catch (e) {
      return '';
    }
  }

  async upgrade() {
    const lines = this.readLines();
    if (!lines.length) {
      console.warn('[cart-upgrade] no eligible lines to convert (empty data-cart-upgrade-lines)');
      return;
    }

    const adds = [];
    for (const l of lines) {
      const plan = this.planFor(l);
      if (!plan) {
        this.showError('No subscription plan available.');
        return;
      }
      adds.push({ id: l.variant, quantity: l.quantity, selling_plan: Number(plan) });
    }

    this.setLoading(true);
    this.clearError();

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
    const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    // Use XHR, not fetch. Shopify's shop_events_listener (and some apps) wrap
    // window.fetch to publish cart events — and a buggy analytics web pixel throws
    // on those events, which breaks the awaited fetch and aborts the conversion.
    // XHR isn't wrapped, so the cart calls run cleanly.
    const post = (url, data) =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.onload = () => {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            resolve({});
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(JSON.stringify(data));
      });

    try {
      // 1. Remove the one-time line(s). Keys are current as of the server render
      //    and nothing has mutated the cart yet, so they're valid.
      const updates = {};
      lines.forEach((l) => {
        updates[l.key] = 0;
      });
      await post(updateUrl, { updates });

      // 2. Add the subscription version(s) and render the drawer in the same call.
      const addBody = { items: adds };
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        addBody.sections = cartEl.getSectionsToRender().map((s) => s.id).join(',');
        addBody.sections_url = window.location.pathname;
      }
      const res = await post(addUrl, addBody);
      if (res && res.status) {
        // The 30-pack add failed — restore the original 10-pack subscription so nothing is lost.
        await post(addUrl, {
          items: lines.map((l) => {
            const it = { id: l.orig || l.variant, quantity: l.quantity };
            if (l.origPlan) it.selling_plan = Number(l.origPlan);
            return it;
          }),
        }).catch(() => {});
        throw new Error(res.description || res.message || 'Could not add subscription.');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upgrade', cartData: res });
      }
      if (cartEl) {
        cartEl.classList.remove('is-empty');
        try {
          if (typeof cartEl.renderContents === 'function') cartEl.renderContents(res);
          else if (typeof cartEl.open === 'function') cartEl.open();
        } catch (e) {
          if (typeof cartEl.open === 'function') cartEl.open();
        }
      } else {
        window.location.reload();
      }
    } catch (e) {
      this.showError(e && e.message ? e.message : 'Could not upgrade. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(on) {
    const btn = this.querySelector('[data-cart-upgrade-btn]');
    const spinner = this.querySelector('[data-cart-upgrade-spinner]');
    btn?.toggleAttribute('aria-disabled', on);
    btn?.classList.toggle('loading', on);
    spinner?.classList.toggle('hidden', !on);
  }

  showError(msg) {
    const el = this.querySelector('[data-cart-upgrade-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  clearError() {
    const el = this.querySelector('[data-cart-upgrade-error]');
    if (el) el.hidden = true;
  }
}

if (!customElements.get('cart-upgrade')) customElements.define('cart-upgrade', CartUpgrade);
